import React, { useState } from 'react';
import { Alert, Box, Button, Card, CardContent, CardHeader, Chip, CircularProgress, Grid, Stack, Typography } from '@mui/material';
import { Sync as SyncIcon } from '@mui/icons-material';
import { httpsCallable } from 'firebase/functions';
import { format } from 'date-fns';
import { functions } from '../../../shared/utils/firebase';
import { useSyncMeta } from '../../staffPerformance/hooks/useStaffPerformance';
import { nextScheduledRuns, periodLabel } from '../../staffPerformance/utils/metrics';

interface SyncResult {
  status: 'ok' | 'error';
  counts?: { staff?: number };
  error?: string | null;
}

interface Outcome {
  severity: 'success' | 'warning' | 'error';
  message: string;
}

const Stat: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Grid item xs={6} sm={4}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="subtitle1" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
      {value}
    </Typography>
  </Grid>
);

/**
 * Admin Panel → Data Sync. Shows the last staff-performance sync and runs the
 * same Cloud Function the weekly / monthly schedules use (also the first load).
 */
const StaffPerformanceSyncCard: React.FC = () => {
  const { meta, error: metaError } = useSyncMeta();
  const [running, setRunning] = useState(false);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const runs = nextScheduledRuns(new Date());

  const handleSync = async () => {
    setRunning(true);
    setOutcome(null);
    try {
      const trigger = httpsCallable<{ force?: boolean }, SyncResult>(functions, 'triggerStaffPerformanceSync');
      const { data } = await trigger({});
      if (data.status === 'ok') {
        setOutcome({ severity: 'success', message: `Sync finished: ${data.counts?.staff ?? 0} staff documents written` });
      } else {
        setOutcome({ severity: 'warning', message: data.error || 'The sync did not run.' });
      }
    } catch (error) {
      console.error('[staff-performance] manual sync failed', error);
      setOutcome({ severity: 'error', message: error instanceof Error ? error.message : 'Sync failed. Check the function logs.' });
    } finally {
      setRunning(false);
    }
  };

  const statusChip = !meta ? (
    <Chip size="small" label="Never synced" />
  ) : meta.status === 'ok' ? (
    <Chip size="small" color="success" label="Last sync succeeded" />
  ) : (
    <Chip size="small" color="error" label="Last sync failed" />
  );

  const lastRun = meta?.synced_at
    ? `${format(meta.synced_at, 'EEE d MMM yyyy, HH:mm')} · ${meta.trigger} · ${Math.round(meta.duration_ms / 1000)} s`
    : 'Run "Sync now" to load the staff performance data for the first time.';
  const lastGood =
    meta?.status === 'error' && meta.last_success_at
      ? `Data on the page is from the last successful run: ${format(meta.last_success_at, 'EEE d MMM yyyy, HH:mm')}.`
      : null;
  const shownPeriods = meta?.last_success_periods ?? meta?.periods ?? null;

  return (
    <Card>
      <CardHeader
        avatar={<SyncIcon />}
        title="Staff performance data"
        subheader="Mirror of the BigQuery staff appraisal views that the Staff Performance page reads"
        action={
          <Button
            variant="contained"
            onClick={handleSync}
            disabled={running}
            startIcon={running ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
            sx={{ mt: 1, mr: 1 }}
          >
            Sync now
          </Button>
        }
      />
      <CardContent>
        <Stack spacing={2}>
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
            {statusChip}
            <Typography variant="body2" color="text.secondary">
              {lastRun}
            </Typography>
          </Stack>

          {metaError ? <Alert severity="error">{metaError}</Alert> : null}
          {meta?.status === 'error' && meta.error ? (
            <Alert severity="error">
              {meta.error}
              {lastGood ? ` ${lastGood}` : ''}
            </Alert>
          ) : null}
          {outcome ? <Alert severity={outcome.severity}>{outcome.message}</Alert> : null}

          {meta ? (
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
              <Grid container spacing={2}>
                <Stat label="Staff documents" value={meta.counts.staff} />
                <Stat label="With client-change data" value={meta.counts.withClients} />
                <Stat label="With conversion data" value={meta.counts.withConversion} />
                <Stat
                  label="Source rows skipped"
                  value={
                    <>
                      {meta.counts.skippedRows}{' '}
                      <Typography component="span" variant="caption" color="text.secondary">
                        no staff id
                      </Typography>
                    </>
                  }
                />
                <Stat label="Latest 6 months" value={periodLabel(shownPeriods?.latest)} />
                <Stat label="Prior 6 months" value={periodLabel(shownPeriods?.prior)} />
              </Grid>
            </Box>
          ) : null}

          <Typography variant="body2" color="text.secondary">
            Next runs: <b>{format(runs.weekly, 'EEE d MMM, HH:mm')}</b> (weekly) · <b>{format(runs.monthly, 'EEE d MMM, HH:mm')}</b> (monthly)
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Sync now runs the same job as the schedule. It is safe to repeat: every run replaces all staff documents and removes staff
            no longer in the views.
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default StaffPerformanceSyncCard;
